/* Vitae Build Your Own configurator.
 * State machine over 4 steps (Base -> Mouthpiece -> Percolator optional
 * -> Connector). Connector ring quantity is derived, never user-set:
 * 1 (base+mouthpiece joint) + 1 per percolator.
 * Interaction model mirrors the Next.js demo: animated step entry,
 * done-checkmark tabs, Back/Next controls, selection check badges.
 */
(() => {
  const root = document.querySelector('[data-vitae-configurator]');
  if (!root) return;

  const dataEl = document.querySelector('[data-vitae-configurator-data]');
  let stepData;
  try {
    stepData = JSON.parse(dataEl.textContent);
  } catch (e) {
    console.error('Vitae configurator: bad product data', e);
    return;
  }
  const products = Object.fromEntries(stepData.map((s) => [s.key, s.products]));

  const parseJson = (sel) => {
    const el = document.querySelector(sel);
    if (!el) return [];
    try { return JSON.parse(el.textContent); } catch { return []; }
  };
  const presets = parseJson('[data-vitae-configurator-presets]');
  const upsells = parseJson('[data-vitae-configurator-upsells]').filter((u) => u.available);

  const state = {
    base: null,
    mouthpiece: null,
    connector: null,
    percolators: [],
    percolatorDecided: false,
  };

  const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
  const money = (cents) =>
    moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, (cents / 100).toFixed(2));

  const STEP_ORDER = ['base', 'mouthpiece', 'percolator', 'connector'];
  const tabs = Object.fromEntries(
    [...root.querySelectorAll('[data-step-tab]')].map((el) => [el.dataset.stepTab, el])
  );
  const panels = Object.fromEntries(
    [...root.querySelectorAll('[data-step-panel]')].map((el) => [el.dataset.stepPanel, el])
  );
  let activeStep = 'base';

  const ringQuantity = () => 1 + state.percolators.length;

  /* Build state survives navigation (PDP detours, back button) via
     sessionStorage; cleared once the build lands in the cart. */
  const STORE_KEY = 'vitae-build-state';
  function saveState() {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify({ ...state, activeStep }));
    } catch { /* private mode; harmless */ }
  }
  function restoreState() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(STORE_KEY));
      if (!saved) return null;
      state.base = saved.base || null;
      state.mouthpiece = saved.mouthpiece || null;
      state.connector = saved.connector || null;
      state.percolators = Array.isArray(saved.percolators) ? saved.percolators : [];
      state.percolatorDecided = !!saved.percolatorDecided;
      return saved.activeStep || null;
    } catch { return null; }
  }
  function clearState() {
    try { sessionStorage.removeItem(STORE_KEY); } catch { /* noop */ }
  }

  /* When the current build exactly matches a preset's components,
     the shopper gets the actual bundle product: bundle price shown
     everywhere and the bundle variant added to cart. Any swap turns
     it back into a component-priced custom build. */
  function presetMatchesState(preset) {
    if (!state.base || !preset.base || state.base.id !== preset.base.id) return false;
    if (!state.mouthpiece || !preset.mouthpiece || state.mouthpiece.id !== preset.mouthpiece.id) return false;
    if (!state.connector || !preset.connector || state.connector.id !== preset.connector.id) return false;
    const want = [preset.percolator, preset.percolator2].filter(Boolean).map((p) => p.id).sort();
    const have = state.percolators.map((p) => p.id).sort();
    return want.length === have.length && want.every((id, i) => id === have[i]);
  }
  function matchingBundle() {
    const preset = presets.find((p) => p.bundle && p.bundle.available && presetMatchesState(p));
    return preset ? preset.bundle : null;
  }

  const stepDone = (key) => {
    if (key === 'percolator') return state.percolatorDecided || state.percolators.length > 0;
    return !!state[key];
  };

  const CHECK_SVG = '<svg width="8" height="6" viewBox="0 0 8 6" fill="none" aria-hidden="true"><path d="M1 3L3 5L7 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"/></svg>';

  function renderGrid(key) {
    const grid = root.querySelector(`[data-step-grid="${key}"]`);
    if (!grid) return;
    const isPerc = key === 'percolator';
    grid.innerHTML = (products[key] || [])
      .map((p, i) => {
        const count = isPerc ? state.percolators.filter((x) => x.id === p.id).length : 0;
        const selected = isPerc ? count > 0 : state[key] && state[key].id === p.id;
        return `
          <div class="vitae-config-item">
            <button type="button" class="vitae-config-card${selected ? ' is-selected' : ''}"
                    data-pick="${key}" data-index="${i}" ${p.available ? '' : 'disabled'}>
              <span class="vitae-config-card__media">
                ${p.image ? `<img src="${p.image}" alt="" loading="lazy">` : ''}
                ${selected && !isPerc ? `<span class="vitae-config-card__check">${CHECK_SVG}</span>` : ''}
                ${isPerc && count ? `<span class="vitae-config-card__count">×${count}</span>` : ''}
              </span>
              <span class="vitae-config-card__title">${p.title}</span>
              <span class="vitae-config-card__price">${p.available ? money(p.price) : 'Sold out'}${p.preorder ? ' <span class="vitae-config-card__preorder">Pre-order</span>' : ''}</span>
              ${isPerc && count ? `<span class="vitae-config-card__remove" data-remove-perc="${p.id}">Remove one</span>` : ''}
            </button>
            <button type="button" class="vitae-config-card__details" data-details="${key}" data-index="${i}" aria-label="Details for ${p.title.replace(/"/g, '&quot;')}">Details</button>
          </div>`;
      })
      .join('');
  }

  function saveStateSoon(step) {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify({ ...state, activeStep: step || activeStep }));
    } catch { /* noop */ }
  }

  function applyPick(key, product) {
    if (key === 'percolator') {
      state.percolators.push(product);
      state.percolatorDecided = true;
    } else {
      state[key] = product;
    }
    renderGrid(key);
    renderSummary();
    renderTabs();
    renderNav();
    saveState();
    confirmPick(key, (products[key] || []).findIndex((p) => p.id === product.id));
    if (key !== 'percolator') setTimeout(() => advanceFrom(key), 900);
  }

  /* "Added to build" flash on the just-picked card: visible
     confirmation before the step auto-advances. */
  function confirmPick(key, index) {
    const grid = root.querySelector(`[data-step-grid="${key}"]`);
    const picked = grid && grid.querySelector(`[data-pick][data-index="${index}"]`);
    if (picked) picked.classList.add('is-confirm');
  }

  let quickviewCtx = null;
  function openQuickview(key, index, fromBuild) {
    const p = products[key][index];
    const qv = document.querySelector('[data-quickview]');
    if (!p || !qv) return;
    quickviewCtx = { key, index, fromBuild: !!fromBuild };
    /* Swap only makes sense when inspecting a piece of the current
       build: it jumps to that piece's step in the builder. */
    qv.querySelector('[data-qv-swap]').hidden = !fromBuild;
    const qvImg = qv.querySelector('[data-qv-image]');
    if (p.image) qvImg.src = p.image; else qvImg.removeAttribute('src');
    qv.querySelector('[data-qv-title]').textContent = p.title;
    qv.querySelector('[data-qv-price]').textContent = (p.available ? money(p.price) : 'Sold out') + (p.preorder ? ' - Pre-order' : '');
    /* Stamped badge, filled by vitae-stamped-badges.js; hidden by CSS
       when the product has no reviews */
    qv.querySelector('[data-qv-rating]').innerHTML = p.product_id
      ? `<span class="vitae-stamped"><span class="stamped-product-reviews-badge" data-id="${p.product_id}"></span></span>`
      : '';
    /* Attribute ratings chart (ease of cleaning, durability, difficulty)
       from product metafields; rows render only when data exists. */
    const attrsEl = qv.querySelector('[data-qv-attrs]');
    const attrs = Object.entries(p.attrs || {}).filter(([, v]) => Number(v) >= 1);
    if (attrs.length) {
      attrsEl.innerHTML = attrs
        .map(([label, v]) => {
          const n = Math.min(5, Math.round(Number(v)));
          const dots = Array.from({ length: 5 }, (_, i) =>
            `<span class="vitae-qv-dot${i < n ? ' is-on' : ''}"></span>`
          ).join('');
          return `<div class="vitae-qv-attr"><span>${label}</span><span class="vitae-qv-dots">${dots}</span></div>`;
        })
        .join('');
      attrsEl.hidden = false;
    } else {
      attrsEl.hidden = true;
    }
    qv.querySelector('[data-qv-desc]').textContent = p.description || '';
    qv.querySelector('[data-qv-link]').href = p.url;
    qv.querySelector('[data-qv-select]').disabled = !p.available;
    qv.hidden = false;
    document.body.style.overflow = 'hidden';
  }
  function closeQuickview() {
    const qv = document.querySelector('[data-quickview]');
    if (qv) { qv.hidden = true; document.body.style.overflow = ''; }
  }

  function renderTabs() {
    STEP_ORDER.forEach((k, i) => {
      const tab = tabs[k];
      const done = stepDone(k);
      const active = k === activeStep;
      tab.classList.toggle('is-active', active);
      tab.classList.toggle('is-done', done && !active);
      const idx = STEP_ORDER.indexOf(activeStep);
      tab.disabled = !done && i > idx;
      tab.querySelector('[data-step-num]').innerHTML = done && !active ? CHECK_SVG : String(i + 1);
    });
  }

  function renderNav() {
    STEP_ORDER.forEach((k, i) => {
      const panel = panels[k];
      const next = panel.querySelector('[data-step-next]');
      const nextLabel = panel.querySelector('[data-next-label]');
      const isLast = i === STEP_ORDER.length - 1;
      if (k === 'percolator') {
        next.disabled = false;
        nextLabel.textContent = state.percolators.length ? 'Next' : 'Skip - no percolator';
      } else if (isLast) {
        next.disabled = !stepDone(k);
        nextLabel.textContent = 'Complete build';
      } else {
        next.disabled = !stepDone(k);
        nextLabel.textContent = 'Next';
      }
    });
  }

  let transitioning = false;

  function goTo(key, animate = true) {
    if (key === activeStep) return;
    saveStateSoon(key);
    const fromPanel = panels[activeStep];
    const toPanel = panels[key];
    activeStep = key;
    renderTabs();
    renderNav();

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!animate || reduced || transitioning) {
      STEP_ORDER.forEach((k) => panels[k].classList.toggle('is-active', k === key));
      fromPanel.classList.remove('is-exiting', 'is-entering');
      return;
    }

    /* Demo parity (AnimatePresence mode="wait"): exit the current panel
       (fade + 20px left), then enter the next (fade + from 20px right). */
    transitioning = true;
    fromPanel.classList.add('is-exiting');
    fromPanel.addEventListener('animationend', () => {
      fromPanel.classList.remove('is-active', 'is-exiting');
      toPanel.classList.add('is-active', 'is-entering');
      toPanel.addEventListener('animationend', () => {
        toPanel.classList.remove('is-entering');
        transitioning = false;
      }, { once: true });
    }, { once: true });
  }

  function renderSummary() {
    const list = root.querySelector('[data-summary-list]');
    const rows = [];
    let total = 0;

    /* Same top-to-bottom order as the assembled piece (see buildRows). */
    if (state.mouthpiece) { rows.push([state.mouthpiece.title, state.mouthpiece.price]); total += state.mouthpiece.price; }
    state.percolators.forEach((p) => { rows.push([p.title, p.price]); total += p.price; });
    if (state.base) { rows.push([state.base.title, state.base.price]); total += state.base.price; }
    if (state.connector) {
      const qty = ringQuantity();
      rows.push([`${state.connector.title} × ${qty}`, state.connector.price * qty]);
      total += state.connector.price * qty;
    }

    list.innerHTML = rows
      .map(([t, c]) => `<li><span>${t}</span><span>${money(c)}</span></li>`)
      .join('');
    const bundle = matchingBundle();
    root.querySelector('[data-summary-total]').textContent = rows.length ? money(bundle ? bundle.price : total) : '-';
    const bundleNote = root.querySelector('[data-bundle-note]');
    if (bundleNote) bundleNote.hidden = !bundle;
    root.querySelector('[data-rings-note]').hidden = !state.connector || state.percolators.length === 0;
    root.querySelector('[data-config-atc]').disabled = !(state.base && state.mouthpiece && state.connector);
    /* Keep the preset-card highlight in sync with whatever the
       build currently matches. */
    const matchIdx = presets.findIndex((p) => p.bundle && p.bundle.available && presetMatchesState(p));
    root.querySelectorAll('[data-preset-card]').forEach((c) =>
      c.classList.toggle('is-selected', Number(c.dataset.presetCard) === matchIdx)
    );
  }

  function advanceFrom(key) {
    const idx = STEP_ORDER.indexOf(key);
    if (idx < STEP_ORDER.length - 1) goTo(STEP_ORDER[idx + 1]);
  }

  /* Load a preset bundle's components into the build state and
     re-render everything. Returns the preset, or null if the
     mapping is incomplete. */
  function applyPreset(index) {
    const preset = presets[index];
    if (!preset || !preset.base || !preset.mouthpiece || !preset.connector) return null;
    state.base = preset.base;
    state.mouthpiece = preset.mouthpiece;
    state.connector = preset.connector;
    state.percolators = [preset.percolator, preset.percolator2].filter(Boolean);
    state.percolatorDecided = true;
    STEP_ORDER.forEach(renderGrid);
    renderSummary();
    renderTabs();
    renderNav();
    saveState();
    return preset;
  }

  /* Fresh start after a build lands in the cart. */
  function resetBuilder() {
    state.base = null;
    state.mouthpiece = null;
    state.connector = null;
    state.percolators = [];
    state.percolatorDecided = false;
    clearState();
    root.querySelectorAll('[data-preset-card]').forEach((c) => c.classList.remove('is-selected'));
    STEP_ORDER.forEach(renderGrid);
    renderSummary();
    renderTabs();
    renderNav();
    goTo('base', false);
  }

  /* Jump to a step and bring the step strip into view. */
  function revealStep(key) {
    goTo(key);
    const steps = root.querySelector('.vitae-config__steps');
    if (steps) steps.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  root.addEventListener('click', (e) => {
    const removeBtn = e.target.closest('[data-remove-perc]');
    if (removeBtn) {
      e.stopPropagation();
      const id = Number(removeBtn.dataset.removePerc);
      const idx = state.percolators.findIndex((p) => p.id === id);
      if (idx > -1) state.percolators.splice(idx, 1);
      renderGrid('percolator');
      renderGrid('connector');
      renderSummary();
      renderTabs();
      renderNav();
      return;
    }

    const card = e.target.closest('[data-pick]');
    if (card) {
      const key = card.dataset.pick;
      const index = Number(card.dataset.index);
      const product = products[key][index];
      if (key === 'percolator') {
        state.percolators.push(product);
        state.percolatorDecided = true;
      } else {
        state[key] = product;
      }
      renderGrid(key);
      renderSummary();
      renderTabs();
      renderNav();
      /* Confirmation beat: an "Added" flash on the picked card, then
         mandatory single-pick steps auto-advance; percolators stay
         put so multiples can stack. */
      confirmPick(key, index);
      if (key !== 'percolator') {
        setTimeout(() => advanceFrom(key), 900);
      }
      return;
    }

    const tab = e.target.closest('[data-step-tab]');
    if (tab && !tab.disabled) { goTo(tab.dataset.stepTab); return; }

    const back = e.target.closest('[data-step-back]');
    if (back && !back.disabled) {
      const idx = STEP_ORDER.indexOf(back.closest('[data-step-panel]').dataset.stepPanel);
      if (idx > 0) goTo(STEP_ORDER[idx - 1]);
      return;
    }

    const next = e.target.closest('[data-step-next]');
    if (next && !next.disabled) {
      const key = next.closest('[data-step-panel]').dataset.stepPanel;
      if (key === 'percolator') state.percolatorDecided = true;
      if (key === 'connector') {
        playAssembleCinematic(() => showBuildModal('Your build, assembled.'));
      } else {
        advanceFrom(key);
      }
      renderTabs();
      renderNav();
      return;
    }

    const presetBtn = e.target.closest('[data-preset]');
    if (presetBtn) {
      /* Customize in builder: load the components, land on step 1
         with every piece pre-selected so the shopper can swap parts;
         the summary aside carries add-to-cart. */
      if (applyPreset(Number(presetBtn.dataset.preset))) revealStep('base');
      return;
    }

    const presetAtc = e.target.closest('[data-preset-atc]');
    if (presetAtc) {
      const id = Number(presetAtc.dataset.presetAtc);
      presetAtc.disabled = true;
      presetAtc.textContent = 'Adding…';
      fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
      })
        .then((res) => {
          if (!res.ok) throw new Error('add failed');
          presetAtc.textContent = 'Added ✓';
          showUpsellModal();
        })
        .catch(() => { presetAtc.textContent = 'Try again'; presetAtc.disabled = false; });
      return;
    }

    /* Anywhere else on a preset card: load the components and open
       the build modal so the bundle's pieces can be inspected.
       Cards without a component mapping link to the product page. */
    const presetCard = e.target.closest('[data-preset-card], [data-preset-url]');
    if (presetCard) {
      if (presetCard.dataset.presetUrl) {
        window.location.href = presetCard.dataset.presetUrl;
        return;
      }
      const preset = applyPreset(Number(presetCard.dataset.presetCard));
      if (preset) showBuildModal(preset.title);
      return;
    }

    const detailsBtn = e.target.closest('[data-details]');
    if (detailsBtn) {
      openQuickview(detailsBtn.dataset.details, Number(detailsBtn.dataset.index));
      return;
    }

  });

  /* Discount codes applied so far through the upsell prompt. The
     codes are ordinary admin discounts named UPSELL<pct>; applying
     them here is what scopes the deal to the configurator flow -
     Shopify itself has no "came through the modal" condition. */
  const appliedUpsellCodes = new Set();
  function applyUpsellDiscount(code) {
    if (!code) return;
    if (appliedUpsellCodes.has(code)) return;
    appliedUpsellCodes.add(code);
    const base = window.Shopify?.routes?.root || '/';
    /* Newer Cart AJAX API accepts discount codes on update; the
       /discount/CODE endpoint sets the checkout cookie and is the
       works-everywhere fallback. Both are idempotent, so fire both. */
    fetch(`${base}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: [...appliedUpsellCodes].join(',') }),
    })
      .catch(() => { /* fall through to the cookie endpoint */ })
      .finally(() => {
        fetch(`${base}discount/${encodeURIComponent(code)}`).catch(() => { /* cookie set is best-effort */ });
      });
  }

  document.addEventListener('click', (e) => {
    /* Upsell add lives on the document listener: the post-cart modal
       sits outside the configurator root, so a root-scoped handler
       never receives its clicks. */
    const upsellAdd = e.target.closest('[data-upsell-add]');
    if (upsellAdd) {
      const id = Number(upsellAdd.dataset.upsellAdd);
      const code = upsellAdd.dataset.upsellCode || '';
      upsellAdd.disabled = true;
      upsellAdd.textContent = 'Adding…';
      fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
      })
        .then((res) => {
          upsellAdd.textContent = res.ok ? 'Added ✓' : 'Try again';
          if (!res.ok) { upsellAdd.disabled = false; return; }
          applyUpsellDiscount(code);
        })
        .catch(() => { upsellAdd.textContent = 'Try again'; upsellAdd.disabled = false; });
      return;
    }
    if (e.target.closest('[data-modal-close]')) {
      const modal = document.querySelector('[data-config-modal]');
      if (modal) { modal.hidden = true; document.body.style.overflow = ''; }
    }
    if (e.target.closest('[data-build-close]')) closeBuildModal();
    /* Per-row controls in the build preview: swap jumps straight to
       that piece's step; remove (percolators only - the other pieces
       are required) drops the piece, re-derives the ring quantity,
       and re-renders the preview in place. */
    const bSwap = e.target.closest('[data-brow-swap]');
    if (bSwap) {
      closeBuildModal();
      revealStep(bSwap.dataset.browSwap);
      return;
    }
    const bRemove = e.target.closest('[data-brow-remove]');
    if (bRemove) {
      const idx = Number(bRemove.dataset.browRemove);
      if (idx > -1 && idx < state.percolators.length) state.percolators.splice(idx, 1);
      STEP_ORDER.forEach(renderGrid);
      renderSummary();
      renderTabs();
      renderNav();
      saveState();
      showBuildModal(document.querySelector('[data-bmodal-title]').textContent);
      return;
    }
    const bRow = e.target.closest('[data-brow-key]');
    if (bRow) {
      openQuickview(bRow.dataset.browKey, Number(bRow.dataset.browIdx), true);
      return;
    }
    if (e.target.closest('[data-qv-swap]') && quickviewCtx) {
      const swapKey = quickviewCtx.key;
      closeQuickview();
      closeBuildModal();
      revealStep(swapKey);
      return;
    }
    const bAtc = e.target.closest('[data-bmodal-atc]');
    if (bAtc) {
      closeBuildModal();
      const atc = root.querySelector('[data-config-atc]');
      if (atc && !atc.disabled) atc.click();
    }
    if (e.target.closest('[data-quickview-close]')) closeQuickview();
    if (e.target.closest('[data-qv-select]') && quickviewCtx) {
      const { key, index } = quickviewCtx;
      closeQuickview();
      applyPick(key, products[key][index]);
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { closeQuickview(); closeBuildModal(); }
    /* Keyboard activation for the role="button" cards and rows. */
    if ((e.key === 'Enter' || e.key === ' ') && e.target.matches && e.target.matches('[data-preset-card], [data-brow-key]')) {
      e.preventDefault();
      e.target.click();
    }
  });

  /* "Your build" modal, opened after the step flow's Complete build
     cinematic: items stagger in on open, rows open the quick view,
     primary adds the build to cart. */
  const stepKeyFor = (item) => {
    for (const key of STEP_ORDER) {
      const idx = (products[key] || []).findIndex((p) => p.id === item.id);
      if (idx > -1) return { key, idx };
    }
    return null;
  };

  /* Rows mirror the assembled piece top to bottom: mouthpiece up
     top, percolators in the middle, base at the bottom. Connector
     rings come last as the joining hardware. */
  function buildRows() {
    return [
      { tag: 'Mouthpiece', item: state.mouthpiece, qty: 1 },
      ...state.percolators.map((p, i) => ({ tag: state.percolators.length > 1 ? `Percolator ${i + 1}` : 'Percolator', item: p, qty: 1, percIndex: i })),
      { tag: 'Base', item: state.base, qty: 1 },
      { tag: 'Connector ring', item: state.connector, qty: ringQuantity() },
    ];
  }

  /* "Complete build" cinematic: the chosen component images fly in
     from beyond the viewport edges and converge into a stack at
     center, then an impact burst plays (glow core, cluster pulse,
     shockwave rings, sparks, "Assembled" title) before handing off
     to the build modal or add-to-cart. Native Web Animations API
     only - no library payload. Click or Escape skips straight to
     the result; reduced-motion users skip the cinematic entirely. */
  function playAssembleCinematic(done) {
    /* How long the assembled stack stays on screen after the impact
       burst, before fading out. Tunable in the theme editor. */
    const holdMs = parseInt(root.dataset.cineHold, 10) || 2350;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rows = buildRows().filter((r) => r.item && r.item.image);
    if (reduced || rows.length < 2 || !('animate' in Element.prototype)) { done(); return; }

    const cine = document.createElement('div');
    cine.className = 'vitae-cine';
    cine.innerHTML =
      '<div class="vitae-cine__backdrop"></div>' +
      '<div class="vitae-cine__stage">' +
        '<div class="vitae-cine__core"></div>' +
        '<div class="vitae-cine__cluster"></div>' +
        '<p class="vitae-cine__title">Assembled</p>' +
      '</div>';
    document.body.appendChild(cine);
    document.body.style.overflow = 'hidden';

    let finished = false;
    const onKey = (e) => { if (e.key === 'Escape') finish(); };
    function finish() {
      if (finished) return;
      finished = true;
      document.removeEventListener('keydown', onKey);
      cine.remove();
      document.body.style.overflow = '';
      done();
    }
    cine.addEventListener('click', finish);
    document.addEventListener('keydown', onKey);

    const stage = cine.querySelector('.vitae-cine__stage');
    const cluster = cine.querySelector('.vitae-cine__cluster');
    cine.querySelector('.vitae-cine__backdrop')
      .animate([{ opacity: 0 }, { opacity: 1 }], { duration: 250, fill: 'forwards' });

    /* Phase 1: convergence into a vertical stack that mirrors the
       assembled piece. Rows are already ordered mouthpiece ->
       percolators -> base, so index order IS top-to-bottom; the
       connector ring (always last) docks beside the stack as the
       joining hardware. */
    const n = rows.length;
    const glassCount = Math.max(1, n - 1);
    const radius = Math.min(window.innerWidth, window.innerHeight) * 0.42 + 80;
    const STAGGER = 140;
    const FLY = 900;
    /* Piece size comes from CSS (--cine-piece, responsive); measure
       the first piece so the stack and spark reach scale with it. */
    let pieceSize = 96;
    let spacing = 70;
    rows.forEach((r, i) => {
      const piece = document.createElement('div');
      piece.className = 'vitae-cine__piece';
      piece.innerHTML =
        `<img src="${r.item.image}" alt="">` +
        `<span class="vitae-cine__piece-check">${CHECK_SVG}</span>` +
        (r.qty > 1 ? `<span class="vitae-cine__piece-qty">×${r.qty}</span>` : '') +
        '<span class="vitae-cine__piece-ring"></span>';
      cluster.appendChild(piece);
      if (i === 0) {
        pieceSize = piece.offsetWidth || pieceSize;
        /* Frameless photos stack flush (a 2px overlap hides seams),
           compressed further if the full stack would not fit the
           viewport height. */
        spacing = Math.max(
          pieceSize * 0.45,
          Math.min(pieceSize - 2, (window.innerHeight * 0.55 - pieceSize) / Math.max(1, glassCount - 1))
        );
      }
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2 + 0.4;
      const sx = Math.cos(angle) * radius;
      const sy = Math.sin(angle) * radius;
      const spin = (i % 2 ? 1 : -1) * (16 + i * 4);
      const isRing = i === n - 1 && n > 1;
      let gx;
      let gy;
      let gs;
      let grot;
      if (isRing) {
        gx = pieceSize * 0.92;
        gy = 0;
        gs = 0.72;
        grot = 8;
      } else {
        /* Dead-straight column so the photos read as one piece. */
        gx = 0;
        gy = (i - (glassCount - 1) / 2) * spacing;
        gs = 1;
        grot = 0;
      }
      piece.animate(
        [
          { transform: `translate(${sx}px, ${sy}px) rotate(${spin}deg) scale(0.45)`, opacity: 0 },
          { opacity: 1, offset: 0.3 },
          { transform: `translate(${gx}px, ${gy}px) rotate(${grot}deg) scale(${gs})`, opacity: 1 },
        ],
        { duration: FLY, delay: 150 + i * STAGGER, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' }
      );
      /* Landing beat: a check badge pops on and the card border
         pulses once as each piece clicks into place. */
      const landAt = 150 + i * STAGGER + FLY - 200;
      piece.querySelector('.vitae-cine__piece-check').animate(
        [
          { opacity: 0, transform: 'scale(0)' },
          { opacity: 1, transform: 'scale(1.3)', offset: 0.6 },
          { opacity: 1, transform: 'scale(1)' },
        ],
        { duration: 320, delay: landAt, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)', fill: 'both' }
      );
      piece.querySelector('.vitae-cine__piece-ring').animate(
        [
          { opacity: 0.8, transform: 'scale(0.6)' },
          { opacity: 0, transform: 'scale(1.5)' },
        ],
        { duration: 500, delay: landAt, easing: 'ease-out', fill: 'forwards' }
      );
      /* Quantity pill (e.g. rings ×3) pops in just after landing. */
      const qtyEl = piece.querySelector('.vitae-cine__piece-qty');
      if (qtyEl) {
        qtyEl.animate(
          [
            { opacity: 0, transform: 'translateY(4px) scale(0.8)' },
            { opacity: 1, transform: 'translateY(0) scale(1)' },
          ],
          { duration: 300, delay: landAt + 120, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)', fill: 'both' }
        );
      }
    });

    /* The stack is taller than the old cluster: push the title (and
       keep the glow sized) below its real half-height. */
    const stackHalf = (pieceSize + spacing * Math.max(0, glassCount - 1)) / 2;
    cine.querySelector('.vitae-cine__title').style.top = `calc(50% + ${Math.round(stackHalf + 28)}px)`;

    /* Phase 2: impact, timed to the last piece's arrival. */
    const impactAt = 150 + (n - 1) * STAGGER + FLY - 140;
    cine.querySelector('.vitae-cine__core').animate(
      [
        { opacity: 0, transform: 'scale(0.4)' },
        { opacity: 0.9, transform: 'scale(1)', offset: 0.3 },
        { opacity: 0, transform: 'scale(1.6)' },
      ],
      { duration: 900, delay: impactAt, easing: 'ease-out', fill: 'both' }
    );
    cluster.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(1.09)', offset: 0.35 },
        { transform: 'scale(1)' },
      ],
      { duration: 420, delay: impactAt, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }
    );
    for (let k = 0; k < 3; k++) {
      const ring = document.createElement('div');
      ring.className = 'vitae-cine__ring';
      stage.appendChild(ring);
      ring.animate(
        [
          { opacity: 0.9, transform: 'scale(0.3)' },
          { opacity: 0, transform: `scale(${2.2 + k * 0.8})` },
        ],
        { duration: 800 + k * 150, delay: impactAt + k * 90, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' }
      );
    }
    for (let k = 0; k < 16; k++) {
      const spark = document.createElement('div');
      spark.className = 'vitae-cine__spark';
      stage.appendChild(spark);
      const a = Math.random() * Math.PI * 2;
      const d = (90 + Math.random() * 130) * (pieceSize / 96);
      const s = 0.5 + Math.random();
      spark.animate(
        [
          { opacity: 1, transform: `translate(0, 0) scale(${s})` },
          { opacity: 0, transform: `translate(${Math.cos(a) * d}px, ${Math.sin(a) * d}px) scale(0.1)` },
        ],
        { duration: 600 + Math.random() * 400, delay: impactAt + 40, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' }
      );
    }
    cine.querySelector('.vitae-cine__title').animate(
      [
        { opacity: 0, letterSpacing: '0.7em', transform: 'translateY(8px)' },
        { opacity: 1, letterSpacing: '0.35em', transform: 'translateY(0)' },
      ],
      { duration: 650, delay: impactAt + 180, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'both' }
    );

    /* Phase 3: fade out and hand off. */
    const out = cine.animate(
      [{ opacity: 1 }, { opacity: 0 }],
      { duration: 320, delay: impactAt + holdMs, easing: 'ease-in', fill: 'forwards' }
    );
    out.onfinish = finish;
  }

  function showBuildModal(title) {
    const modal = document.querySelector('[data-build-modal]');
    if (!modal || !state.base || !state.mouthpiece || !state.connector) return;
    const rows = buildRows();
    let total = 0;
    modal.querySelector('[data-bmodal-items]').innerHTML = rows
      .map((r, i) => {
        const line = r.item.price * r.qty;
        total += line;
        const ref = stepKeyFor(r.item);
        return `
          <div class="vitae-bmodal-item" ${ref ? `data-brow-key="${ref.key}" data-brow-idx="${ref.idx}" role="button" tabindex="0"` : ''} style="animation-delay:${120 + i * 110}ms">
            ${r.item.image ? `<img src="${r.item.image}" alt="" loading="lazy" width="64" height="64">` : ''}
            <div class="vitae-bmodal-item__info">
              <span class="vitae-bmodal-item__tag">${r.tag}${r.qty > 1 ? ` × ${r.qty}` : ''}</span>
              <p>${r.item.title}</p>
              <span class="vitae-bmodal-item__acts">
                ${ref ? `<button type="button" data-brow-swap="${ref.key}">Swap</button>` : ''}
                ${r.percIndex !== undefined ? `<button type="button" data-brow-remove="${r.percIndex}">Remove</button>` : ''}
              </span>
            </div>
            <span class="vitae-bmodal-item__price">${money(line)}</span>
          </div>`;
      })
      .join('');
    const bundle = matchingBundle();
    modal.querySelector('[data-bmodal-total]').textContent = money(bundle ? bundle.price : total);
    const bnote = modal.querySelector('[data-bmodal-note]');
    if (bnote) bnote.hidden = !bundle;
    const bundleLink = modal.querySelector('[data-bmodal-bundle-link]');
    if (bundleLink) {
      bundleLink.hidden = !(bundle && bundle.url);
      if (bundle && bundle.url) bundleLink.href = bundle.url;
    }
    modal.querySelector('[data-bmodal-title]').textContent = title || 'Your build';
    const primary = modal.querySelector('[data-bmodal-atc]');
    primary.textContent = 'Add build to cart';
    primary.disabled = false;
    modal.classList.add('vitae-assemble-run');
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
  }
  function closeBuildModal() {
    const modal = document.querySelector('[data-build-modal]');
    if (modal) { modal.hidden = true; document.body.style.overflow = ''; }
  }

  function showUpsellModal() {
    const modal = document.querySelector('[data-config-modal]');
    if (!modal) { window.location.href = `${window.Shopify?.routes?.root || '/'}cart`; return; }
    const wrap = modal.querySelector('[data-modal-items]');
    if (upsells.length && !wrap.childElementCount) {
      wrap.innerHTML = upsells
        .map((u) => `
          <div class="vitae-config-modal__item">
            ${u.image ? `<img src="${u.image}" alt="" loading="lazy" width="72" height="72">` : ''}
            <div class="vitae-config-modal__item-info">
              <p>${u.title}</p>
              <span>${u.pct > 0 && u.code
                ? `<s class="vitae-upsell-was">${money(u.price)}</s> ${money(Math.round(u.price * (100 - u.pct) / 100))} <span class="vitae-upsell-chip">-${u.pct}%</span>`
                : money(u.price)}</span>
            </div>
            <button type="button" class="vitae-config-modal__add" data-upsell-add="${u.id}" data-upsell-code="${u.pct > 0 && u.code ? u.code : ''}">Add</button>
          </div>`)
        .join('');
    }
    if (!upsells.length) modal.querySelector('.vitae-config-modal__sub').hidden = true;
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  root.querySelector('[data-config-atc]').addEventListener('click', async (ev) => {
    const btn = ev.currentTarget;
    const errEl = root.querySelector('[data-config-error]');
    errEl.hidden = true;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    /* A build that exactly matches a bundle ships AS the bundle -
       same product, same price as the preset card's add-to-cart. */
    const bundle = matchingBundle();
    let items;
    if (bundle) {
      items = [{ id: bundle.id, quantity: 1 }];
    } else {
      items = [
        { id: state.base.id, quantity: 1 },
        { id: state.mouthpiece.id, quantity: 1 },
        { id: state.connector.id, quantity: ringQuantity() },
      ];
      const percCounts = {};
      state.percolators.forEach((p) => { percCounts[p.id] = (percCounts[p.id] || 0) + 1; });
      Object.entries(percCounts).forEach(([id, quantity]) => items.push({ id: Number(id), quantity }));
    }

    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error((await res.json()).description || 'Could not add to cart');
      btn.disabled = false;
      btn.textContent = btn.dataset.label || 'Add build to cart';
      resetBuilder();
      showUpsellModal();
    } catch (err) {
      errEl.textContent = err.message;
      errEl.hidden = false;
      btn.disabled = false;
      btn.textContent = btn.dataset.label || 'Add build to cart';
    }
  });

  const restoredStep = restoreState();
  STEP_ORDER.forEach(renderGrid);
  renderSummary();
  renderTabs();
  renderNav();
  if (restoredStep && restoredStep !== 'base' && panels[restoredStep]) {
    goTo(restoredStep, false);
  }
})();
